import React from "react";
import type { SecurityView, Tone } from "@shared/site-audit-report-view";
import { ReportSection } from "./section";

export function SecurityCoverage({
  security,
  showCve = true,
}: {
  security: SecurityView;
  /** The public risk scan sets this false. Staff mode alone is not enough. */
  showCve?: boolean;
}) {
  if (security.state === "missing" || security.state === "error") {
    return (
      <ReportSection id="wordpress" title="WordPress">
        <p className={security.state === "error" ? "text-sm text-destructive" : "text-sm text-foreground/75"}>
          {security.message}
        </p>
      </ReportSection>
    );
  }

  if (security.state === "unscored") {
    return (
      <ReportSection id="wordpress" title="WordPress">
        <p className="text-sm leading-relaxed">{security.message}</p>
        <p className="text-sm text-foreground/75">{security.bandLabel}</p>
        <WarningList warnings={security.warnings} />
        {security.attribution ? <p className="text-xs text-foreground/75">{security.attribution}</p> : null}
      </ReportSection>
    );
  }

  const fails = security.failCount === 1 ? "1 fail" : `${security.failCount} fails`;
  const barLabel = security.segments.map((segment) => `${segment.label} ${segment.count}`).join(", ");

  return (
    <ReportSection id="wordpress" title="WordPress" hint={security.coverageLine}>
      <p className={`text-sm font-semibold ${toneClass(security.tone)}`}>
        {security.bandLabel} · {fails}
      </p>
      {security.feedStale ? <p className="text-sm text-foreground/75">{security.clearLabel}</p> : null}
      {security.segments.length > 0 ? (
        <>
          <div className="flex h-3 overflow-hidden rounded-full bg-muted" role="img" aria-label={`${barLabel}. ${fails}.`}>
            {security.segments.map((segment) => (
              <div
                key={segment.id}
                className={segmentClass(segment.id)}
                style={{ width: `${segment.share * 100}%` }}
              />
            ))}
          </div>
          <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-foreground/75">
            {security.segments.map((segment) => (
              <li key={segment.id}>
                <span className={`mr-1 inline-block h-2 w-2 rounded-full ${segmentClass(segment.id)}`} />
                {segment.label} {segment.count}
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {(security.coreVersion || security.phpVersion) && (
        <p className="text-sm text-foreground/75">
          {security.coreVersion ? `WordPress ${security.coreVersion}` : "WordPress version hidden"}
          {security.phpVersion ? ` · PHP ${security.phpVersion}` : ""}
        </p>
      )}
      {security.components.length > 0 ? (
        <ul className="space-y-3">
          {security.components.map((component, index) => (
            <li key={`${component.type}-${index}`} className="border-t border-border pt-3 text-sm">
              <p>
                <span className="font-medium">
                  {component.name}
                  {component.version ? ` ${component.version}` : ""}
                </span>
                <span className={component.tone === "problem" ? " text-destructive" : " text-foreground/75"}>
                  {" "}
                  · {component.statusLabel}
                </span>
              </p>
              {component.wording ? <p className="mt-1 text-foreground/75">{component.wording}</p> : null}
              {component.findings.map((finding, findingIndex) => (
                <p key={findingIndex} className="mt-1 text-foreground/75">
                  {finding.wording || finding.title}
                  {showCve && finding.cve ? ` · ${finding.cve}` : ""}
                </p>
              ))}
            </li>
          ))}
        </ul>
      ) : null}
      <WarningList warnings={security.warnings} />
      {security.attribution ? <p className="text-xs text-foreground/75">{security.attribution}</p> : null}
    </ReportSection>
  );
}

function WarningList({ warnings }: { warnings: string[] }) {
  if (warnings.length === 0) return null;
  return (
    <ul className="space-y-1">
      {warnings.map((warning, index) => (
        <li key={index} className="text-sm text-foreground/75">{warning}</li>
      ))}
    </ul>
  );
}

function toneClass(tone: Tone): string {
  if (tone === "good") return "text-chart-2";
  if (tone === "watch" || tone === "act") return "text-chart-3";
  if (tone === "urgent" || tone === "problem") return "text-destructive";
  return "text-foreground/75";
}

function segmentClass(id: "checked" | "version_hidden" | "unchecked"): string {
  if (id === "checked") return "bg-chart-2";
  if (id === "version_hidden") return "bg-chart-3";
  return "bg-foreground/25";
}
